import { css } from '@emotion/css';
import { formatTrustedDate, formatTrustedTime } from '../../../../shared/utils/datetime';
import type { HTMLAttributes } from 'react';
import { FiCalendar, FiClock, FiMapPin, FiShoppingBag, FiUser, FiMail, FiPhone, FiCreditCard, FiAlertCircle } from 'react-icons/fi';
import { useGetUserOrderById, useGetOrderPayment } from '../../hooks';
import { useQuery } from '@tanstack/react-query';
import { eventsService } from '../../../../api/services/events';
import Button from '../../../../shared/ui/button/button';

// Styles matching profile modules
const container = css({
  backgroundColor: 'var(--primary-color)',
  borderRadius: 'var(--spacing-sm)',
  padding: 'var(--spacing-lg)',
  boxShadow: 'var(--shadow-200)',
  '@media (max-width: 480px)': { padding: 'var(--spacing-md)' },
  '@media (max-width: 360px)': { padding: 'var(--spacing-sm)' },
  '@media print': {
    padding: 'var(--spacing-md)',
    margin: 0,
    boxShadow: 'none',
    borderRadius: 0,
    pageBreakAfter: 'auto',
  },
});

const header = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 'var(--spacing-lg)',
  '@media (max-width: 480px)': { flexDirection: 'column', alignItems: 'flex-start', gap: 'var(--spacing-sm)' },
});

const title = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-xs)',
  fontSize: 'var(--fs-450)',
  fontWeight: 600,
  color: 'var(--g-800)',
  marginBottom: 0,
  '@media (max-width: 480px)': { fontSize: 'var(--fs-400)' },
});

const body = css({
  fontSize: 'var(--fs-350)',
  color: 'var(--g-700)',
});

const statusBadge = css({
  fontSize: 'var(--fs-300)',
  fontWeight: 500,
  padding: 'var(--spacing-3xs) var(--spacing-xs)',
  borderRadius: 'var(--spacing-3xs)',
});

const statusSuccess = css({
  backgroundColor: 'rgba(72, 187, 120, 0.1)',
  color: 'rgb(39, 103, 73)',
});

const statusPending = css({
  backgroundColor: 'rgba(251, 191, 36, 0.1)',
  color: 'rgb(146, 64, 14)',
});

const statusFailed = css({
  backgroundColor: 'rgba(245, 101, 101, 0.1)',
  color: 'rgb(155, 44, 44)',
});

const statusDefault = css({
  backgroundColor: 'rgba(var(--g-rgb-500), 0.1)',
  color: 'var(--g-600)',
});

const section = css({
  marginBottom: 'var(--spacing-lg)',
  paddingBottom: 'var(--spacing-lg)',
  borderBottom: '1px solid var(--g-100)',
  '&:last-child': { borderBottom: 'none', marginBottom: 0, paddingBottom: 0 },
});

const sectionTitle = css({
  fontSize: 'var(--fs-400)',
  fontWeight: 600,
  color: 'var(--g-800)',
  marginBottom: 'var(--spacing-md)',
});

const eventInfo = css({
  marginBottom: 'var(--spacing-md)',
});

const eventName = css({
  fontSize: 'var(--fs-450)',
  fontWeight: 600,
  color: 'var(--g-800)',
  marginBottom: 'var(--spacing-sm)',
});

const eventMeta = css({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--spacing-md)',
  '@media (max-width: 480px)': { flexDirection: 'column', gap: 'var(--spacing-xs)' },
});

const metaItem = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-3xs)',
  fontSize: 'var(--fs-350)',
  color: 'var(--g-600)',
});

const grid = css({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
  gap: 'var(--spacing-md)',
});

const field = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-3xs)',
});

const label = css({
  fontSize: 'var(--fs-300)',
  color: 'var(--g-500)',
});

const value = css({
  fontSize: 'var(--fs-350)',
  fontWeight: 500,
  color: 'var(--g-800)',
});

const ticketsList = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-sm)',
});

const ticketItem = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: 'var(--spacing-sm)',
  backgroundColor: 'var(--g-100)',
  borderRadius: 'var(--spacing-2xs)',
  '@media (max-width: 480px)': { flexDirection: 'column', alignItems: 'flex-start', gap: 'var(--spacing-xs)' },
});

const ticketDetails = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-3xs)',
});

const ticketName = css({
  fontSize: 'var(--fs-350)',
  fontWeight: 500,
  color: 'var(--g-800)',
});

const ticketMeta = css({
  fontSize: 'var(--fs-300)',
  color: 'var(--g-600)',
});

const ticketPrice = css({
  fontSize: 'var(--fs-400)',
  fontWeight: 600,
  color: 'var(--pc-700)',
});

const summary = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-sm)',
  padding: 'var(--spacing-md)',
  backgroundColor: 'var(--g-100)',
  borderRadius: 'var(--spacing-2xs)',
});

const summaryRow = css({
  display: 'flex',
  justifyContent: 'space-between',
  fontSize: 'var(--fs-350)',
  color: 'var(--g-700)',
});

const summaryTotal = css({
  display: 'flex',
  justifyContent: 'space-between',
  fontSize: 'var(--fs-400)',
  fontWeight: 600,
  color: 'var(--g-800)',
  paddingTop: 'var(--spacing-sm)',
  borderTop: '1px solid var(--g-200)',
});

const actions = css({
  display: 'flex',
  gap: 'var(--spacing-sm)',
  justifyContent: 'flex-end',
  '@media (max-width: 480px)': { flexDirection: 'column' },
  '@media print': { display: 'none' },
});

const loadingState = css({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: 'var(--spacing-2xl)',
  color: 'var(--g-600)',
  fontSize: 'var(--fs-400)',
});

const errorState = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 'var(--spacing-md)',
  padding: 'var(--spacing-lg)',
  backgroundColor: 'rgba(245, 101, 101, 0.1)',
  borderRadius: 'var(--spacing-xs)',
  border: '1px solid rgba(245, 101, 101, 0.3)',
  color: 'rgb(155, 44, 44)',
});

export interface ProfileOrderDetailsProps extends HTMLAttributes<HTMLDivElement> {
  orderId: string;
}

function useScheduleDetail(scheduleId?: string) {
  return useQuery({
    queryKey: ['schedule-detail', scheduleId],
    queryFn: async () => {
      if (!scheduleId) return null;
      const { schedule } = await eventsService.getScheduleById(scheduleId);
      return schedule;
    },
    enabled: !!scheduleId,
  });
}

export function ProfileOrderDetails({ orderId, className, style, ...props }: ProfileOrderDetailsProps) {
  const { data: order, isLoading, isError } = useGetUserOrderById(orderId);
  const { data: payment } = useGetOrderPayment(orderId);

  // Get schedule details for first item to display event info
  const firstScheduleId = order?.items?.[0]?.scheduleId;
  const { data: schedule } = useScheduleDetail(firstScheduleId);

  const handlePrint = () => {
    window.print();
  };

  const formatCurrency = (value?: string | number) => {
    if (!value) return 'R$ 0,00';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(num);
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '—';
    return formatTrustedDate(dateString);
  };

  const formatTime = (dateString?: string | null) => {
    if (!dateString) return '—';
    return formatTrustedTime(dateString);
  };

  const getStatusBadge = (status?: string) => {
    const statusClass = 
      status === 'SUCCESS' ? statusSuccess :
      status === 'PENDING' ? statusPending :
      status === 'FAILED' || status === 'CANCELLED' ? statusFailed :
      statusDefault;
    
    const statusText = 
      status === 'SUCCESS' ? 'Pago' :
      status === 'PENDING' ? 'Pendente' :
      status === 'FAILED' ? 'Falhou' :
      status === 'CANCELLED' ? 'Cancelado' :
      status || 'Desconhecido';

    return <span className={`${statusBadge} ${statusClass}`}>{statusText}</span>;
  };

  const getPaymentMethodLabel = (method?: string) => {
    if (!method) return 'Não especificado';
    if (method === 'credit_card' || method === 'creditCard') return 'Cartão de Crédito';
    if (method === 'debit_card' || method === 'debitCard') return 'Cartão de Débito';
    if (method === 'pix') return 'PIX';
    return method;
  };

  if (isLoading) {
    return (
      <section className={container}>
        <div className={loadingState}>Carregando detalhes do pedido...</div>
      </section>
    );
  }

  if (isError || !order) {
    return (
      <section className={container}>
        <div className={errorState}>
          <FiAlertCircle size={32} />
          <p>Não foi possível carregar os detalhes do pedido.</p>
        </div>
      </section>
    );
  }

  const eventTitle = schedule?.event?.title || 'Evento não especificado';
  const locationName = schedule?.location?.name || 'Local a definir';
  const eventDate = schedule?.date;

  return (
    <section id="order-details-print-section" className={container} style={style} {...props}>
      <div className={header}>
        <h2 className={title}>
          <FiShoppingBag size={20} />
          Detalhes da Compra
        </h2>
        {getStatusBadge(order.status)}
      </div>

      <div className={body}>
        {/* Event Information */}
        <div className={section}>
          <div className={eventInfo}>
            <h3 className={eventName}>{eventTitle}</h3>
            <div className={eventMeta}>
              <div className={metaItem}>
                <FiCalendar size={16} />
                <span>{formatDate(eventDate)}</span>
              </div>
              <div className={metaItem}>
                <FiClock size={16} />
                <span>{formatTime(eventDate)}</span>
              </div>
              <div className={metaItem}>
                <FiMapPin size={16} />
                <span>{locationName}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Order Information */}
        <div className={section}>
          <h4 className={sectionTitle}>Informações do Pedido</h4>
          <div className={grid}>
            <div className={field}>
              <span className={label}>Nº do Pedido</span>
              <span className={value}>#{order.id.substring(0, 8)}</span>
            </div>
            <div className={field}>
              <span className={label}>Data da Compra</span>
              <span className={value}>{formatDate(order.createdAt)}</span>
            </div>
            {order.paidAt && (
              <div className={field}>
                <span className={label}>Data do Pagamento</span>
                <span className={value}>{formatDate(order.paidAt)}</span>
              </div>
            )}
            {order.invoice?.paymentId && (
              <div className={field}>
                <span className={label}>ID da Transação</span>
                <span className={value}>#{order.invoice.paymentId}</span>
              </div>
            )}
          </div>
        </div>

        {/* Tickets/Items */}
        {order.items && order.items.length > 0 && (
          <div className={section}>
            <h4 className={sectionTitle}>Ingressos</h4>
            <div className={ticketsList}>
              {order.items.map((item, index) => (
                <div key={index} className={ticketItem}>
                  <div className={ticketDetails}>
                    <div className={ticketName}>{item.name || item.ticketType || 'Ingresso'}</div>
                    <div className={ticketMeta}>
                      Quantidade: {item.quantity}
                      {item.seats && item.seats.length > 0 && ` • Zona: ${item.seats[0].zone}`}
                      {item.seats && item.seats.length > 0 && item.seats[0].row && ` • Fileira: ${item.seats[0].row}`}
                      {item.seats && item.seats.length > 0 && item.seats[0].column && ` • Assento: ${item.seats[0].column}`}
                    </div>
                  </div>
                  <div className={ticketPrice}>{formatCurrency(item.unitPrice)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Customer Information */}
        {order.guestInfo && (
          <div className={section}>
            <h4 className={sectionTitle}>Informações do Cliente</h4>
            <div className={grid}>
              {order.guestInfo.name && (
                <div className={field}>
                  <span className={label}><FiUser size={14} /> Nome</span>
                  <span className={value}>{order.guestInfo.name}</span>
                </div>
              )}
              {order.guestInfo.email && (
                <div className={field}>
                  <span className={label}><FiMail size={14} /> E-mail</span>
                  <span className={value}>{order.guestInfo.email}</span>
                </div>
              )}
              {order.guestInfo.phone && (
                <div className={field}>
                  <span className={label}><FiPhone size={14} /> Telefone</span>
                  <span className={value}>{order.guestInfo.phone}</span>
                </div>
              )}
              {order.guestInfo.document && (
                <div className={field}>
                  <span className={label}>CPF/CNPJ</span>
                  <span className={value}>{order.guestInfo.document}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Payment Information */}
        <div className={section}>
          <h4 className={sectionTitle}>Informações de Pagamento</h4>
          <div className={grid}>
            <div className={field}>
              <span className={label}><FiCreditCard size={14} /> Método de Pagamento</span>
              <span className={value}>{getPaymentMethodLabel(order.invoice?.paymentMethod)}</span>
            </div>
            {payment && payment.status && (
              <div className={field}>
                <span className={label}>Status do Pagamento</span>
                <span className={value}>{payment.status}</span>
              </div>
            )}
          </div>

          <div className={summary}>
            <div className={summaryRow}>
              <span>Subtotal</span>
              <span>{formatCurrency(order.totalAmount)}</span>
            </div>
            <div className={summaryTotal}>
              <span>Total</span>
              <span>{formatCurrency(order.totalAmount)}</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className={actions}>
          <Button onClick={handlePrint} style={{ background: 'var(--g-700)', borderColor: 'var(--g-700)' }}>
            Imprimir Comprovante
          </Button>
        </div>
      </div>
    </section>
  );
}

export default ProfileOrderDetails;
